import React, { ReactElement, useEffect, useState } from 'react';
import { Box } from 'rebass/styled-components';

import { Backdrop, CloseButton } from './styles';

import { SlidePanel, EmptyState, Card, FormInput } from 'ui/patterns';
import { Tree as TreeNew } from 'ui/patterns/tree';
import { Button, Dropzone, Stack } from 'ui/components';

import { useGetBlobFiles } from 'hooks';

import NoDataImage from 'assets/images/NoDataImage';
import * as Icon from 'assets/icons';

import { apiBaseUrl } from 'utils/urls';
import { showErrorToast, showSuccessToast } from 'utils/showToast';
import { isValidFile, getEncodedFilePath, accept } from 'utils/fileUtils';

import { BlobType } from 'types/procedureBuilder';
import { FileImageProps } from './fileImageTypes';
import { ErrorModal } from 'types/modalTypes';

const toBase64 = (file: File): any => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

function FileImage({
  accountId,
  showPanel,
  togglePanel: toggle,
  account,
  createFolder,
  uploadFile,
  deleteFile,
  addFileToEditor,
  buttonText,
  filesOnly,
}: FileImageProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  const [showAddFolder, setShowAddFolder] = useState<BlobType>();
  const [showFeedback, setShowFeedback] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState<BlobType>();
  const [selectedFile, setSelectedFile] = useState<BlobType>();
  const [folderName, setFolderName] = useState<string>('');
  const [fileToUpload, setFileToUpload] = useState<File>();

  const { data: blobFiles } = useGetBlobFiles({
    accountId,
    uniqueId: account,
  });

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  function togglePanel() {
    setIsVisible(false);
    setTimeout(() => {
      toggle();
    }, 500);
  }

  const toggleAddFolder = (folder?: BlobType) => {
    setShowAddFolder(folder);
  };

  const toggleFileUpload = (folder?: BlobType) => {
    setShowFileUpload(folder);
  };

  function reset() {
    setFolderName('');
    setFileToUpload(undefined);
    setShowFileUpload(undefined);
    setSelectedFile(undefined);
    setShowAddFolder(undefined);
  }

  function onFileChange(file?: File): void {
    if (!file) {
      setSelectedFile(undefined);
      setFileToUpload(file);
      return;
    }
    const regex = /^[a-zA-Z0-9\s_\\.\-\(\)~'@$;,!=:\[\]]+$/;
    if (!regex.test(file.name)) {
      showErrorToast({
        message: 'Please choose a valid file to upload',
      });
      return;
    } else {
      const fileModules = file.name.split('.');
      const fileExtension = fileModules[fileModules.length - 1];
      if (isValidFile(fileExtension)) {
        setSelectedFile(undefined);
        setFileToUpload(file);
      } else {
        setSelectedFile(undefined);
        showErrorToast({
          message: 'Please choose a valid file to upload',
        });
      }
    }
  }

  const fileimageActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          reset();
        }}
      >
        Cancel
      </Button>
      <Button
        disabled={(() => {
          if (filesOnly) {
            if (selectedFile && selectedFile.Type !== 'folder') {
              return false;
            } else return true;
          } else return !selectedFile;
        })()}
        onClick={() => {
          if (selectedFile) {
            addFileToEditor(selectedFile);
            setFolderName('');
            togglePanel();
          }
        }}
        variant="primary"
      >
        {buttonText || 'Add to procedure'}
      </Button>
    </>
  );

  return (
    <SlidePanel
      open={isVisible}
      onClose={() => {
        togglePanel();
        reset();
      }}
      size="lg"
      title="Select Files"
      actions={fileimageActions()}
    >
      <Stack direction="x" gap={3} height="100%">
        <Card p={3} overflow="hidden auto" width="350px" position="relative">
          <TreeNew
            isHidden={!!showAddFolder || !!showFileUpload}
            account={account}
            setSelectedFile={setSelectedFile}
            files={blobFiles && blobFiles.Data ? blobFiles.Data[0] : undefined}
            selectedFile={selectedFile}
            toggleAddFolder={toggleAddFolder}
            toggleFileUpload={toggleFileUpload}
            deleteFile={(file: BlobType) => {
              deleteFile(
                {
                  accountId: accountId,
                  params: {
                    Container: 'root',
                    Title:
                      file.Type === 'folder'
                        ? `${file?.Path}do-not-delete-this-file.txt`
                        : file.Path,
                  },
                },
                {
                  onSuccess: () => {
                    showSuccessToast({
                      message:
                        file.Type === 'folder'
                          ? 'Folder deleted successfully'
                          : 'File deleted successfully',
                    });
                    setSelectedFile(undefined);
                  },
                },
              );
            }}
          />

          {/* Add Folder */}
          <Backdrop show={!!showAddFolder}>
            <CloseButton
              onClick={() => {
                setFolderName('');
                toggleAddFolder(undefined);
                setShowFeedback(false);
              }}
            >
              <Icon.X />
            </CloseButton>
            <Box
              sx={{
                display: 'grid',
                gridGap: 3,
                gridTemplateColumns: '1fr minmax(50px, 15%)',
                alignItems: 'start',
                width: '300px',
              }}
            >
              <FormInput
                onTextChange={setFolderName}
                value={folderName}
                label="Enter folder name"
                type="text"
                feedback={(() => {
                  if (showFeedback) {
                    const folderNameAlreadyExists = showAddFolder?.Blobs?.find(
                      blobFile =>
                        blobFile.Type === 'folder' &&
                        blobFile.Name === folderName,
                    );
                    if (folderNameAlreadyExists) {
                      return 'Folder name is already exist, Please try with another name';
                    }
                  }
                  return '';
                })()}
              />
              <Button
                variant="primary"
                style={{ height: '35px', marginTop: '20px' }}
                title="Add new folder"
                iconBefore={<Icon.Check title="Add new folder" />}
                disabled={(() => {
                  if (showFeedback) {
                    const folderNameAlreadyExists = showAddFolder?.Blobs?.find(
                      blobFile =>
                        blobFile.Type === 'folder' &&
                        blobFile.Name === folderName,
                    );
                    if (folderNameAlreadyExists) {
                      return true;
                    }
                  }
                  return false;
                })()}
                onClick={() => {
                  if (showAddFolder && folderName) {
                    const folderNameAlreadyExists = showAddFolder?.Blobs?.find(
                      blobFile =>
                        blobFile.Type === 'folder' &&
                        blobFile.Name === folderName,
                    );
                    if (folderNameAlreadyExists) {
                      setShowFeedback(true);
                      return;
                    }
                    createFolder(
                      {
                        params: {
                          Title: `${showAddFolder?.Path}${folderName}/do-not-delete-this-file.txt`,
                          Content: `${showAddFolder?.Path}${folderName}`,
                          Container: `root`,
                          streamContent: '',
                        },
                        accountId: accountId,
                      },
                      {
                        onSuccess: () => {
                          showSuccessToast({
                            message: 'Folder created successfully',
                          });
                          toggleAddFolder(undefined);
                          setFolderName('');
                          setShowFeedback(false);
                        },
                        onError: (error: ErrorModal) => {
                          showErrorToast({
                            message: error.Errors[0].Message,
                          });
                        },
                      },
                    );
                  }
                }}
              />
            </Box>
          </Backdrop>

          {/* Upload File */}
          <Backdrop show={!!showFileUpload}>
            <CloseButton onClick={() => toggleFileUpload(undefined)}>
              <Icon.X />
            </CloseButton>
            <Stack direction="y" gap={3} height="100%">
              <Dropzone
                accept={accept}
                onChange={onFileChange}
                value={fileToUpload?.name}
                flex={1}
                info="Special characters not allowed in fileName(+, #, {, }, `, %, &, /, *, ^)"
              />
              <Button
                variant="primary"
                onClick={async () => {
                  if (fileToUpload) {
                    const base64 = await toBase64(fileToUpload);
                    uploadFile(
                      {
                        accountId,
                        params: {
                          Title: `${showFileUpload?.Path}${fileToUpload.name}`,
                          Container: `root`,
                          Content: `C:\\fakepath\\${showFileUpload?.Path}`,
                          streamContent: base64,
                        },
                      },
                      {
                        onSuccess: () => {
                          showSuccessToast({
                            message: 'File uploaded successfully',
                          });
                          toggleAddFolder(undefined);
                          setShowFeedback(false);
                          setFolderName('');
                          toggleFileUpload();
                          setFileToUpload(undefined);
                        },
                        onError: (error: ErrorModal) => {
                          showErrorToast({
                            message: error.Errors[0].Message,
                          });
                        },
                      },
                    );
                  }
                }}
              >
                Add file
              </Button>
            </Stack>
          </Backdrop>
        </Card>
        <Card
          flex="1"
          position="relative"
          bg="var(--color-neutral-contrast-02)"
        >
          <EmptyState>
            {!selectedFile ? (
              <NoDataImage feedback="Please select a file to preview" />
            ) : null}
            {selectedFile && selectedFile.Type === 'image' ? (
              <img src={selectedFile.Uri} />
            ) : null}
            {selectedFile &&
            selectedFile.Type !== 'image' &&
            selectedFile.Type !== 'folder' ? (
              <iframe
                src={`${apiBaseUrl}${'accounts/procedures/get-external-file?filePath='}${getEncodedFilePath(
                  selectedFile.Uri,
                )}`}
                style={{ width: '100%', height: '100%' }}
              ></iframe>
            ) : null}
          </EmptyState>
        </Card>
      </Stack>
    </SlidePanel>
  );
}

export default FileImage;
